import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import MultiFileUploadContainer from '../../../Components/FileUpload/MultiFileUploadContainer';
import CloseIcon from '@mui/icons-material/Close';
import { AddFiletoFolder, FileUpload } from '../../../api/services';

interface AddFileProps {
  open: boolean;
  onClose: () => void;
  folderId: string | undefined;
}

const MultiFileUploadDrawer: React.FC<AddFileProps> = ({ open, onClose, folderId }) => {
  const queryClient = useQueryClient();
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [uploadingIndex, setUploadingIndex] = useState<number>(-1);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const addFileMutation = useMutation(
    (fileData: { name: string; size: number; url: string; fileType: string, parent: string }) => AddFiletoFolder(fileData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('folderData');
      },
    }
  );

  const handleFileSelect = (selectedFiles: File[]) => {
    setFilesToUpload((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const uploadFiles = async () => {
    const totalFiles = filesToUpload.length;
    let successfulUploads = 0;

    for (let i = 0; i < totalFiles; i++) {
      setUploadingIndex(i);
      const file = filesToUpload[i];

      try {
        // Upload the file
        const response = await FileUpload(`Storage/${folderId}`, file);
        const uploadedFile = {
          name: response.data.originalName,
          url: response.data.url,
          size: response.data.size,
          fileType: file.type.includes('video')
            ? 'video'
            : file.type.includes('application/pdf')
            ? 'document'
            : 'image',
          parent: folderId || '',
        };

        // Add the uploaded file to the folder
        await addFileMutation.mutateAsync(uploadedFile);

        successfulUploads += 1;

        // Update progress
        setUploadProgress(((i + 1) / totalFiles) * 100);
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error(`Failed to upload ${file.name}.`);
      }
    }

    setUploadingIndex(-1);
    setFilesToUpload([]);
    setUploadProgress(0);
    onClose();

    // Show toast after all files are uploaded
    toast.success(`${successfulUploads} file${successfulUploads > 1 ? 's' : ''} uploaded successfully!`);
  };

  const handleCancel = () => {
    setFilesToUpload([]);
    setUploadProgress(0);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: '100%', maxWidth: '400px' } }}
    >
      <Box>
        {/* Header */}
        <Box
          sx={{
            width: '100%',
            height: 60,
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            color: 'white',
            zIndex: (theme) => theme.zIndex.drawer + 10,
          }}
        >
          <Typography variant="h6">Add Files</Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* File Upload Section */}
        <Box sx={{ padding: '20px' }}>
          <MultiFileUploadContainer onFileSelect={handleFileSelect} />

          {/* File Count */}
          {filesToUpload.length > 0 && (
            <Typography sx={{ mt: 2, textAlign: 'center', fontWeight: 'bold' }}>
              {filesToUpload.length} file{filesToUpload.length > 1 ? 's' : ''} selected
            </Typography>
          )}
  
          {/* Upload Progress */}
          {uploadingIndex !== -1 && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography sx={{ mt: 1, textAlign: 'center' }}>
                Uploading file {uploadingIndex + 1} of {filesToUpload.length}
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button variant="outlined" color="error" onClick={handleCancel} disabled={uploadingIndex !== -1}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={uploadFiles}
              disabled={!filesToUpload.length || uploadingIndex !== -1}
              startIcon={uploadingIndex !== -1 ? <CircularProgress size={20} /> : null}
            >
              {uploadingIndex === -1 ? 'Submit Files' : `Uploading...`}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MultiFileUploadDrawer;
